import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { useNotificationStore, Notification } from '../store/NotificationStore';

interface Props {
    onClose: () => void;
    onSelectNotification: (n: Notification) => void;
}

export const NotificationPanel: React.FC<Props> = ({ onClose, onSelectNotification }) => {
    const { notifications, markAsRead } = useNotificationStore();

    const handlePress = (n: Notification) => {
        markAsRead(n.id);
        onClose();
        onSelectNotification(n);
    };

    return (
        <View style={styles.overlay}>
            <TouchableOpacity style={styles.backdrop} onPress={onClose} activeOpacity={1} />
            <View style={styles.panel} accessibilityLabel="Teacher feedback panel">
                <View style={styles.header}>
                    <Text style={styles.title} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Teacher Feedback</Text>
                    <TouchableOpacity
                        onPress={onClose}
                        hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                        accessibilityRole="button"
                        accessibilityLabel="Close feedback panel"
                        accessibilityHint="Double tap to close the feedback list"
                    >
                        <Text style={styles.closeText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✕</Text>
                    </TouchableOpacity>
                </View>

                <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
                    {notifications.length === 0 ? (
                        <Text style={styles.emptyText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>No notifications yet.</Text>
                    ) : (
                        notifications.map((n, index) => (
                            <TouchableOpacity
                                key={n.id || String(index)}
                                style={[styles.item, !n.read && styles.unreadItem]}
                                onPress={() => handlePress(n)}
                                activeOpacity={0.8}
                                accessibilityRole="button"
                                accessibilityLabel={String(n.message || 'Feedback')}
                                accessibilityHint="Double tap to read the full feedback"
                            >
                                <View style={styles.row}>
                                    <Text style={styles.icon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>👩‍🏫</Text>
                                    <View style={styles.textCol}>
                                        <Text style={styles.message} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                            {String(n.message || 'Feedback')}
                                        </Text>
                                        {n.payload?.targetText ? (
                                            <Text style={styles.context} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                                Activity: {String(n.payload.targetText)}
                                            </Text>
                                        ) : null}
                                        <Text style={styles.time} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                            {n.timestamp
                                                ? new Date(n.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                : ''}
                                        </Text>
                                    </View>
                                    {n.payload?.overlay ? (
                                        <Text style={styles.overlayBadge} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✏️</Text>
                                    ) : null}
                                </View>
                            </TouchableOpacity>
                        ))
                    )}
                </ScrollView>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'flex-end',
        zIndex: 999,
    },
    backdrop: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    panel: {
        backgroundColor: '#FFFFFF',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        padding: 24,
        maxHeight: '60%',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
    },
    title: {
        fontSize: 20,
        fontWeight: 'bold',
        color: '#1E293B',
    },
    closeText: {
        fontSize: 24,
        color: '#94A3B8',
        padding: 8,
    },
    list: {
        flexGrow: 0,
    },
    listContent: {
        paddingBottom: 8,
    },
    emptyText: {
        textAlign: 'center',
        color: '#94A3B8',
        fontSize: 16,
        marginTop: 40,
        marginBottom: 40,
    },
    item: {
        padding: 16,
        borderRadius: 12,
        backgroundColor: '#F8FAFC',
        marginBottom: 12,
        borderLeftWidth: 4,
        borderLeftColor: 'transparent',
    },
    unreadItem: {
        backgroundColor: '#F0F9FF',
        borderLeftColor: '#3B82F6',
    },
    row: {
        flexDirection: 'row',
        gap: 12,
    },
    textCol: {
        flex: 1,
    },
    icon: {
        fontSize: 20,
        marginTop: 2,
    },
    message: {
        fontSize: 16,
        color: '#1E293B',
        marginBottom: 4,
        fontWeight: '500',
    },
    context: {
        fontSize: 12,
        color: '#64748B',
        marginBottom: 2,
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    time: {
        fontSize: 11,
        color: '#94A3B8',
    },
    overlayBadge: {
        fontSize: 16,
        alignSelf: 'center',
        paddingLeft: 8,
    },
});
